import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function useUnsavedPageGuard(isDirty: boolean) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);
  const allowLeave = useRef(false);

  useEffect(() => {
    if (!isDirty) return;
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    function onClick(event: MouseEvent) {
      if (allowLeave.current) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[role="alertdialog"]')) return;
      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      event.preventDefault();
      event.stopPropagation();
      pendingAction.current = () => {
        allowLeave.current = true;
        navigate(`${url.pathname}${url.search}${url.hash}`);
      };
      setOpen(true);
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty, location.pathname, location.search, navigate]);

  function requestLeave(action: () => void) {
    if (!isDirty || allowLeave.current) {
      action();
      return;
    }
    pendingAction.current = action;
    setOpen(true);
  }

  function continueEditing() {
    pendingAction.current = null;
    setOpen(false);
  }

  function discardAndLeave() {
    const action = pendingAction.current;
    pendingAction.current = null;
    allowLeave.current = true;
    setOpen(false);
    action?.();
  }

  return { open, requestLeave, continueEditing, discardAndLeave };
}
